import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
// Removed Redux auth selectors - now using useAuth() hook
import { useUserData } from '../../hooks/useUserData';
import { 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Alert,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  HelpOutline as HelpIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Person as PersonIcon
} from '@mui/icons-material';
// Remove direct Firestore imports - use service layer instead
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { updatePassword } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useLocale } from '../../context/LocaleContext';
import { updateUserProfile } from '../../services/userService';
import { setUser } from '../../redux/slices/authSlice';
import { useProfilePictureRefresh } from '../../context/ProfilePictureContext';
import SecuritySettings from './SecuritySettings';

// Styled components to match the standard page header pattern
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const MyAccount = () => {
  const dispatch = useDispatch();
  const { user } = useUserData();
  const { currentLocale, setCurrentLocale, t } = useLocale();
  const { refreshProfilePicture } = useProfilePictureRefresh();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    languagePreference: 'en',
    profilePictureUrl: ''
  });

  // Profile picture upload states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const localeOptions = [
    { value: 'en', label: t('locale.english') },
    { value: 'ar', label: t('locale.arabic') }
  ];

  // File validation constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_DIMENSIONS = { width: 1024, height: 1024 };
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  // Validate image file
  const validateImageFile = (file) => {
    return new Promise((resolve, reject) => {
      // Check file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        reject(new Error(t('account.profilePicture.invalidFileType')));
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        reject(new Error(t('account.profilePicture.fileTooLarge')));
        return;
      }

      // Check image dimensions
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        if (img.width > MAX_DIMENSIONS.width || img.height > MAX_DIMENSIONS.height) {
          reject(new Error(t('account.profilePicture.dimensionsTooLarge')));
          return;
        }
        
        resolve(true);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(t('account.profilePicture.invalidImage')));
      };
      
      img.src = url;
    });
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      await validateImageFile(file);
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError(null);
    } catch (err) {
      setError(err.message);
      setSelectedFile(null);
      setPreviewUrl(null);
    }

    // Reset the input
    event.target.value = '';
  };

  // Upload profile picture
  const handleProfilePictureUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Ensure user is authenticated
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      console.log('Uploading profile picture for user:', auth.currentUser.uid);
      console.log('Selected file:', selectedFile.name, selectedFile.type, selectedFile.size);

      // Delete existing profile picture if it exists
      if (formData.profilePictureUrl) {
        try {
          const oldImageRef = ref(storage, `profile-pictures/${auth.currentUser.uid}`);
          await deleteObject(oldImageRef);
          console.log('Deleted old profile picture');
        } catch (deleteError) {
          console.warn('Failed to delete old profile picture:', deleteError);
        }
      }

      // Upload new profile picture
      const imageRef = ref(storage, `profile-pictures/${auth.currentUser.uid}`);
      console.log('Uploading to path:', `profile-pictures/${auth.currentUser.uid}`);
      
      const uploadTask = uploadBytes(imageRef, selectedFile);
      
      // Simulate upload progress (Firebase Storage doesn't provide real-time progress for uploadBytes)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      await uploadTask;
      clearInterval(progressInterval);
      setUploadProgress(100);
      console.log('Upload completed successfully');

      // Get download URL
      const downloadURL = await getDownloadURL(imageRef);
      console.log('Got download URL:', downloadURL);

      // Update user profile in database with new profile picture URL
      await updateUserProfile(auth.currentUser.uid, {
        profileData: {
          profilePictureUrl: downloadURL
        }
      });
      
      // Update local state
      setFormData(prev => ({
        ...prev,
        profilePictureUrl: downloadURL
      }));

      // ✅ Update Redux state with new profile picture URL
      const updatedUser = {
        ...user,
        profilePictureUrl: downloadURL
      };
      dispatch(setUser(updatedUser));

      // Refresh profile picture in other components
      refreshProfilePicture();

      setSuccess(t('account.profilePicture.uploadedSuccessfully'));
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (err) {
      setError(t('account.profilePicture.uploadFailed'));
      console.error('Profile picture upload failed:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Remove profile picture
  const handleRemoveProfilePicture = async () => {
    if (!formData.profilePictureUrl) return;

    setLoading(true);
    try {
      // Ensure user is authenticated
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      // Delete from storage
      const imageRef = ref(storage, `profile-pictures/${auth.currentUser.uid}`);
      await deleteObject(imageRef);

      // Update user profile in database to remove profile picture URL
      await updateUserProfile(auth.currentUser.uid, {
        profileData: {
          profilePictureUrl: ''
        }
      });
      
      // Update local state
      setFormData(prev => ({
        ...prev,
        profilePictureUrl: ''
      }));

      // ✅ Update Redux state with empty profile picture URL
      const updatedUser = {
        ...user,
        profilePictureUrl: ''
      };
      dispatch(setUser(updatedUser));

      // Refresh profile picture in other components
      refreshProfilePicture();

      setSuccess(t('account.profilePicture.removedSuccessfully'));
    } catch (err) {
      setError(t('account.profilePicture.removeFailed'));
      console.error('Failed to remove profile picture:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Use auth.currentUser for consistency
        const currentUserId = auth.currentUser?.uid || user?.uid;
        if (!currentUserId) {
          throw new Error('No authenticated user found');
        }

        console.log('Fetching user data for:', currentUserId);
        
        // TODO: Replace direct Firestore access with service layer call
        // Example: const userData = await userService.getUserProfile(currentUserId);
        
        console.warn('MyAccount: Direct Firestore access removed - implement service layer call');
        
        // For now, use existing user data until service layer is implemented
        const userData = user || {};
        setFormData({
          name: userData.name || '',
          email: userData.email || user?.email || '',
          phone: userData.phone || '',
          title: userData.title || '',
          languagePreference: userData.languagePreference || currentLocale || 'en',
          profilePictureUrl: userData.profilePictureUrl || ''
        });
        
        /* Original code removed:
        const userDoc = await getDoc(doc(db, 'users', currentUserId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setFormData({
            name: userData.name || '',
            email: userData.email || user?.email || '',
            phone: userData.phone || '',
            title: userData.title || '',
            languagePreference: userData.languagePreference || currentLocale || 'en',
            profilePictureUrl: userData.profilePictureUrl || ''
          });
        }
        */
      } catch (err) {
        setError(t('account.failedToLoadUserData'));
        console.error('Failed to fetch user data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (auth.currentUser || user?.uid) {
      fetchUserData();
    }
  }, [user, currentLocale, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLanguagePreferenceChange = async (e) => {
    const newLanguagePreference = e.target.value;
    setFormData(prev => ({
      ...prev,
      languagePreference: newLanguagePreference
    }));
    
    // Update the locale context immediately using the enhanced function
    setCurrentLocale(newLanguagePreference);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // ✅ Use secure Firebase function instead of direct Firestore access
      const profileData = {
        name: formData.name,
        phone: formData.phone,
        title: formData.title,
        // Note: languagePreference is not in the allowed fields for the Firebase function
        // We'll handle this separately with direct Firestore access
      };

      // Update profile data via secure function
      const result = await updateUserProfile(user.uid, { profileData });
      
      // Update language preference separately (not in secure function)
      if (formData.languagePreference !== user.languagePreference) {
        // TODO: Replace direct Firestore access with service layer call
        // Example: await userService.updateUserLanguagePreference(auth.currentUser.uid, formData.languagePreference);
        
        console.warn('MyAccount: Direct Firestore access removed - implement service layer call');
        
        // For now, update local state only until service layer is implemented
        setFormData(prev => ({
          ...prev,
          languagePreference: formData.languagePreference
        }));
      }

      // ✅ Update Redux state with the updated user data
      const updatedUser = {
        ...user,
        name: result.updatedUser.name,
        phone: result.updatedUser.phone,
        title: formData.title, // title is not returned by the function, use form data
        languagePreference: formData.languagePreference
      };
      dispatch(setUser(updatedUser));

      // Refresh profile picture in case it was updated
      refreshProfilePicture();

      setSuccess(t('account.profileUpdatedSuccessfully'));
    } catch (err) {
      setError(t('account.failedToUpdateProfile'));
      console.error('Profile update failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError(t('account.newPasswordsDoNotMatch'));
      setLoading(false);
      return;
    }

    try {
      await updatePassword(auth.currentUser, passwordForm.newPassword);
      setSuccess(t('account.passwordUpdatedSuccessfully'));
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      setError(t('account.failedToUpdatePassword'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Standard Header */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ fontSize: 32, color: 'white' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                {t('account.title')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {t('account.profileInformation')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Profile Information */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Profile Picture Section - Left Side */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={formData.profilePictureUrl}
                sx={{ 
                  width: 80, 
                  height: 80,
                  border: '2px solid',
                  borderColor: 'divider'
                }}
              >
                {formData.name ? formData.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="caption" color="textSecondary" align="center">
                {t('account.profilePicture.description')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  size="small"
                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                >
                  {t('account.profilePicture.upload')}
                </Button>
                {formData.profilePictureUrl && (
                  <IconButton
                    color="error"
                    onClick={handleRemoveProfilePicture}
                    size="small"
                    disabled={loading}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Form Fields - Right Side */}
          <Grid item xs={12} md={9}>
            <form onSubmit={handleProfileUpdate}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('common.name')}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('common.email')}
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('common.phone')}
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('account.title')}
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('account.myLanguagePreference')}</InputLabel>
                      <Select
                        name="languagePreference"
                        value={formData.languagePreference}
                        onChange={handleLanguagePreferenceChange}
                        label={t('account.myLanguagePreference')}
                      >
                        {localeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title={t('account.languagePreferenceTooltip')}>
                      <IconButton size="small">
                        <HelpIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    size="small"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={18} /> : t('account.updateProfile')}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Paper>

      {/* Change Password */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('account.changePassword')}
        </Typography>
        
        <form onSubmit={handlePasswordUpdate}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={t('account.currentPassword')}
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={t('account.newPassword')}
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label={t('account.confirmNewPassword')}
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                size="small"
                disabled={loading}
              >
                {loading ? <CircularProgress size={18} /> : t('account.changePassword')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Two-Factor Authentication Settings */}
      <SecuritySettings />

      {/* Profile Picture Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1.5 }}>{t('account.profilePicture.uploadDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            {/* File input */}
            <input
              accept="image/jpeg,image/jpg,image/png,image/webp"
              style={{ display: 'none' }}
              id="profile-picture-upload"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="profile-picture-upload">
              <Button
                variant="outlined"
                component="span"
                size="small"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 1.5 }}
              >
                {t('account.profilePicture.selectFile')}
              </Button>
            </label>

            {/* Preview */}
            {previewUrl && (
              <Box sx={{ mt: 1, mb: 1.5 }}>
                <Avatar
                  src={previewUrl}
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    mx: 'auto',
                    border: '2px solid',
                    borderColor: 'divider'
                  }}
                />
              </Box>
            )}

            {/* Upload progress */}
            {uploading && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" gutterBottom>
                  {t('account.profilePicture.uploading')}
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            {/* File requirements */}
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5, display: 'block' }}>
              {t('account.profilePicture.requirements')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 1, px: 2 }}>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading} size="small">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleProfilePictureUpload} 
            variant="contained"
            size="small"
            disabled={!selectedFile || uploading}
          >
            {uploading ? t('account.profilePicture.uploading') : t('account.profilePicture.upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyAccount;